"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, PhoneCall, Droplets, Flame, ShieldAlert, Clock, ArrowRight, CircleCheck as CheckCircle2, Waves } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface EmergencyItem {
  id: string;
  title: string;
  subtitle: string;
  severity: "Critical" | "Urgent" | "High Priority";
  actionStep: string;
  commonSigns: string;
  dispatchTime: string;
}
const emergencyList: EmergencyItem[] = [{
  id: "burst-pipes",
  title: "Burst or Ruptured Pipes",
  subtitle: "Active gushing water, ceiling pooling, or frozen pipe blowouts.",
  severity: "Critical",
  actionStep: "Shut off the main water valve immediately to minimize structural damage.",
  commonSigns: "Hissing sounds behind drywall, sudden drop in water pressure, puddles under flooring.",
  dispatchTime: "Immediate dispatch: Under 45 mins"
}, {
  id: "sewer-backups",
  title: "Severe Sewer Line Backups",
  subtitle: "Raw sewage backing up into bathtubs, floor drains, or multiple toilets.",
  severity: "Critical",
  actionStep: "Stop all water usage and keep family and pets away from contaminated areas.",
  commonSigns: "Foul sulfur odors, bubbling toilets when running sinks, multiple simultaneous clogs.",
  dispatchTime: "24/7 priority emergency crew"
}, {
  id: "water-heater-leak",
  title: "Major Water Heater Failures",
  subtitle: "Tank leaking from bottom, scalding water loss, or electrical burning smells.",
  severity: "Urgent",
  actionStep: "Turn off water supply to the heater and switch off its dedicated circuit breaker.",
  commonSigns: "Pooling around water heater base, knocking noises, no hot water throughout home.",
  dispatchTime: "Same-day rapid replacement"
}, {
  id: "no-water-flow",
  title: "Complete Loss of Water",
  subtitle: "Total supply shutoff or main municipal service line break.",
  severity: "High Priority",
  actionStep: "Check with neighbors to verify if it is an area outage or isolated to your home.",
  commonSigns: "Dry faucets, sputtering air in pipes, saturated yard patches above the main.",
  dispatchTime: "Fast diagnostics & restoration"
}];
export default function Triage() {
  const shouldReduceMotion = useReducedMotion();
  const getSeverityBadgeVariant = (severity: EmergencyItem["severity"]) => {
    switch (severity) {
      case "Critical":
        return "destructive";
      case "Urgent":
        return "default";
      default:
        return "secondary";
    }
  };
  const getIcon = (id: string) => {
    switch (id) {
      case "burst-pipes":
        return <Waves className="size-6 text-destructive" />;
      case "sewer-backups":
        return <ShieldAlert className="size-6 text-destructive" />;
      case "water-heater-leak":
        return <Flame className="size-6 text-primary" />;
      default:
        return <Droplets className="size-6 text-chart-3" />;
    }
  };
  return <section id="emergencyplumbingtriage" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border">
          <div className="max-w-2xl space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider">
              <AlertTriangle className="size-3.5 text-destructive" />
              <span>Emergency Triage Guide</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
              Identify Your Plumbing Emergency
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground">
              Water damage escalates in minutes. Review the scenarios below for immediate safety steps, then call our 24/7 Jonesboro crew for instant dispatch.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
            <Button asChild size="lg" className="bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md font-semibold">
              <a href="tel:4708479076">
                <PhoneCall className="size-4 mr-2" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contact/">
                <span>Request Service</span>
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Triage Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 pt-12">
          {emergencyList.map((item, index) => <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.35,
          delay: index * 0.1
        }} className="h-full">
              <Card className="h-full flex flex-col justify-between border border-border bg-card text-card-foreground shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="p-2.5 rounded-lg bg-secondary flex items-center justify-center">
                      {getIcon(item.id)}
                    </div>
                    <Badge variant={getSeverityBadgeVariant(item.severity)}>
                      {item.severity}
                    </Badge>
                  </div>
                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-default font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground mt-1 text-sm sm:text-base">
                      {item.subtitle}
                    </CardDescription>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 py-2">
                  <div className="p-3.5 rounded-lg bg-secondary/60 border border-border text-sm">
                    <div className="font-semibold text-foreground flex items-center gap-1.5 mb-1">
                      <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                      <span>Immediate First Step:</span>
                    </div>
                    <p className="text-muted-foreground pl-5">
                      {item.actionStep}
                    </p>
                  </div>

                  <div className="text-sm space-y-1">
                    <span className="font-semibold text-foreground">Common Indicators:</span>
                    <p className="text-muted-foreground">
                      {item.commonSigns}
                    </p>
                  </div>
                </CardContent>

                <CardFooter className="pt-4 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs sm:text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="size-4 text-primary shrink-0" />
                    <span>{item.dispatchTime}</span>
                  </div>
                  <Button asChild variant="ghost" size="sm" className="p-0 h-auto font-semibold text-primary hover:text-primary/80 hover:bg-transparent">
                    <a href="tel:4708479076" className="inline-flex items-center gap-1">
                      <span>Dispatch Plumber</span>
                      <ArrowRight className="size-3.5" />
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Banner */}
        <div className="mt-12 rounded-2xl bg-secondary p-6 sm:p-8 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h3 className="text-lg sm:text-xl font-default font-bold text-secondary-foreground">
              Not sure what type of leak or blockage you are dealing with?
            </h3>
            <p className="text-sm text-muted-foreground max-w-xl">
              Our on-call master plumbers can guide you over the phone to contain damage while a technician heads to your Jonesboro address.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href="tel:4708479076">
                <PhoneCall className="size-4 mr-2" />
                <span>Call Now for Guidance</span>
              </a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/problems/">
                <span>View All Problems</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}